// =============================================================================
// domain/ingestKeys.ts — the realtime-sync keys, and how they read on screen.
// -----------------------------------------------------------------------------
// The key itself is minted and hashed in `io/sync/ingestKeys.ts`, which needs
// Web Crypto. Everything here is the pure part: the shape of a key, and the
// sentences the list puts under one.
//
// ── 🚨 WHAT AN INGEST KEY ACTUALLY IS, BECAUSE IT CHANGES EVERY DECISION ──
//
// A long-lived BEARER SECRET. Anyone holding it can POST trades into the
// owner's account, from anywhere, with no other credential. It is shown ONCE at
// creation and stored only as a SHA-256 hash, so there is no recovery path — the
// single remedy for a leak is revoke and regenerate.
//
// That is why the prefix exists: the list has to identify a key without being
// able to show it.
// =============================================================================

/** Every key starts with this, so one is recognisable in a support paste. */
export const KEY_PREFIX = 'tck_';

/**
 * How much of the key is stored for display.
 *
 * ⚠ 12 CHARACTERS — `tck_` plus EIGHT of the secret. Transcribed from the old
 * client rather than chosen, because the two clients write rows the other one
 * lists: a different width here would make keys minted in one app look like a
 * different kind of thing in the other.
 *
 * ⚠ Eight characters of a 32-character secret is not a meaningful leak, and it
 * is the whole reason the list is usable at all. Do not lengthen it.
 */
export const KEY_PREFIX_LENGTH = 12;

export const keyPrefixOf = (plaintext: string): string =>
  plaintext.slice(0, KEY_PREFIX_LENGTH);

/** The platforms the desktop loggers run on. Order is the old client's. */
export const KEY_SOURCES: readonly { value: string; label: string }[] = [
  { value: 'atas', label: 'ATAS' },
  { value: 'sierra', label: 'Sierra Chart' },
  { value: 'other', label: 'Other' },
];

export interface IngestKey {
  id: string;
  keyPrefix: string;
  label: string;
  accountId: string | null;
  source: string;
  createdAt: string | null;
  lastUsedAt: string | null;
}

/** Where the desktop logger POSTs. */
export const ingestEndpoint = (supabaseUrl: string): string =>
  `${String(supabaseUrl ?? '').replace(/\/+$/, '')}/functions/v1/trade-ingest`;

/**
 * What a key is pinned to.
 *
 * ⚠ The UNPINNED case is not "no account" — it is a routing MODE. An unpinned
 * key files each trade by the broker account id the logger reports, which is the
 * behaviour someone running four prop accounts through one logger wants. Showing
 * it as blank would read as a half-configured key.
 */
export function pinLabel(
  key: Pick<IngestKey, 'accountId'>,
  nameOf: (id: string) => string | null,
): string {
  if (!key.accountId) return 'routes by Broker Account ID';
  return `→ ${nameOf(key.accountId) || 'account'}`;
}

/**
 * ⚠ "never used" rather than a blank. A key that has never fired is the single
 * most useful thing to know when a trader says syncing is not working — it
 * separates "the indicator is not talking to us at all" from "it was, and
 * stopped".
 */
export function lastUsedLabel(
  iso: string | null | undefined,
  locale?: string,
): string {
  if (!iso) return 'never used';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'never used';
  return `last used ${d.toLocaleString(locale)}`;
}

/** The label shown when the user gave none. */
export const keyLabelOrPlaceholder = (label: string): string => label.trim() || '(no label)';
